import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Record } from 'pkg/api/models/record';

// A channel is a group chat legacy chat can't show (PLA-718): only chat v2
// asks the API for them, so v1 never holds one.
export type ChatTypes = 'group' | 'personal' | 'channel';

export interface Chat extends Record, AllowedActionable {
	createdAt?: number;
	isArchived?: boolean;
	isMonitored?: boolean;
	isNew: boolean;
	lastMessageId?: number;
	replyToMessageId?: number;
	title?: string;
	type: ChatTypes;
	userId?: number;
	rootGroupId: number;
	// The group a group chat is linked to, whose members the auto-add rules
	// read. Null until an admin links one (or the chat was started from a team).
	groupId?: number | null;
	autoAddParents?: boolean;
	autoAddPlayers?: boolean;
	autoAddStaff?: boolean;
	// Who of the linked group may not send (PLA-720); chat admins always can.
	// Absent from an API that predates channels.
	staffCannotSend?: boolean;
	playersCannotSend?: boolean;
	parentsCannotSend?: boolean;

	lastMessage?: models.chatMessage.ChatMessage;
	users: models.chatUser.ChatUser[];
	// How many members a channel has (PLA-721). Chat v2 asks for slim
	// channels, whose `users` hold only your own membership; group and
	// personal chats, and an API from before it, leave it out.
	memberCount?: number;
}

/**
 *	Asks the API for slim channels on whatever answers with a chat: a
 *	channel then carries `memberCount` and only your own membership, not
 *	its every member. Only channels change, and v1 never gets one.
 */
export const SLIM_CHANNELS_QUERY = 'slimChannels=1';

/** The role families an auto-add rule covers; `staff` is staff and admins together. */
export type AutoAddRole = 'parents' | 'players' | 'staff';

export const AUTO_ADD_ROLES: AutoAddRole[] = ['parents', 'players', 'staff'];

type AutoAddField = 'autoAddParents' | 'autoAddPlayers' | 'autoAddStaff';

/** The chat field each rule is stored in. */
export const AUTO_ADD_FIELDS: { [role in AutoAddRole]: AutoAddField } = {
	parents: 'autoAddParents',
	players: 'autoAddPlayers',
	staff: 'autoAddStaff',
};

/** The role groups a send restriction covers, as `AutoAddRole` names them. */
export type SendRestrictionField =
	| 'staffCannotSend'
	| 'playersCannotSend'
	| 'parentsCannotSend';

/** The chat field each role's send restriction is stored in. */
export const SEND_RESTRICTION_FIELDS: {
	[role in AutoAddRole]: SendRestrictionField;
} = {
	parents: 'parentsCannotSend',
	players: 'playersCannotSend',
	staff: 'staffCannotSend',
};

/**
 *	How many members each rule would add right now, from a dry run. Someone in
 *	two roles is in both counts; `total` counts each person once, and
 *	`userIds` names them (absent from an API that predates it).
 */
export type AutoAddCounts = { [role in AutoAddRole]: number } & {
	total: number;
	userIds?: number[];
	/**
	 *	How big the chat would be with the rules' members in, and the most a
	 *	rule-filled chat may be (PLA-719); absent from an API that predates
	 *	them.
	 */
	memberCount?: number;
	memberLimit?: number;
};

export interface CreatePayload {
	title: string;
	groupId: number;
	type: ChatTypes;
	users: number[];
	// The team a group chat keeps in sync with. Defaults, on the API, to
	// groupId when that is not the organization.
	linkedGroupId?: number;
	// The rules reach the linked group's subgroups too, which makes the chat
	// a channel (PLA-719): a club's or a section's.
	includeSubgroups?: boolean;
	autoAddParents?: boolean;
	autoAddPlayers?: boolean;
	autoAddStaff?: boolean;
	// Any of these on creates a channel (PLA-720); needs a linked team.
	staffCannotSend?: boolean;
	playersCannotSend?: boolean;
	parentsCannotSend?: boolean;
}

export interface UpdateChatPayload {
	title?: string;
	isArchived?: boolean;
	groupId?: number;
	autoAddParents?: boolean;
	autoAddPlayers?: boolean;
	autoAddStaff?: boolean;
	// Turning one on makes a group chat a channel, for good (PLA-720).
	staffCannotSend?: boolean;
	playersCannotSend?: boolean;
	parentsCannotSend?: boolean;
}

/** WhatsApp's ticks: see `messageReceipt`. */
export type MessageReceipt = 'sent' | 'delivered' | 'read';

/** A chat the viewer's app has received messages in, up to `messageId`. */
export interface Delivery {
	chatId: number;
	messageId: number;
}

/** When one recipient got as far as a message; `at` is null when unknown. */
export interface MessageReceiptEntry {
	userId: number;
	at: number | null;
}

/**
 *	Message info for one of the viewer's own messages: who has read it, who
 *	it has reached without being read, and how many it hasn't reached.
 */
export interface MessageReceipts {
	read: MessageReceiptEntry[];
	delivered: MessageReceiptEntry[];
	waiting: number;
}
